import { BrandType } from 'api/brand/schema';
import {
  applyColors,
  applyFavicon,
  applyFont,
  POST_MESSAGE_EVENTS,
} from 'api/brand/utils';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createContext, useContext } from 'react';
import theme from 'shared/theme/custom-theme';
import { navigation } from 'shared/util/navigation';

interface Colors {
  primary: {
    100: string;
  };
  text: {
    white: string;
    primary: string;
    dark: string;
    disabled: string;
    secondary: string;
  };
  background: {
    medium: string;
    dark: string;
    row: string;
    readonly: string;
    baseSurface: string;
    baseSurface1: string;
    baseSurface2: string;
    baseSurface3: string;
    primary: string;
  };
  bar: {
    priority1: string;
    priority2: string;
    priority3: string;
  };
  donut: {
    techCheckedIn: string;
    techAssigned: string;
    pendingTechAssignment: string;
    pendingCustomer: string;
    onHold: string;
  };
  stroke: {
    readonly: string;
    secondary: string;
  };
  content: {
    readonly: string;
    error: string;
  };
  border: {
    light: string;
    hr: string;
    error: string;
  };
  button: {
    primary: string;
    primaryHover: string;
    disabled: string;
    hoverColor: string;
    tertiary: string;
    blue: string;
  };
  tab: {
    active: string;
  };
  iconButton: {
    primary: {
      backgroundColor: string;
      color: string;
      hover: string;
      disabled: string;
      active: string;
    };
    secondary: {
      borderColor: string;
      color: string;
      hover: string;
      hoverColor: string;
      disabled: string;
      active: string;
    };
    destructive: {
      borderColor: string;
      backgroundColor: string;
      color: string;
      hover: string;
      disabled: string;
      active: string;
    };
    ghost: {
      color: string;
      hover: string;
      disabled: string;
      active: string;
    };
  };
  scroll: {
    background: string;
    thumb: string;
  };
  segment: {
    light: string;
    primary: string;
    tertiary: string;
    blue: string;
  };
  status: {
    status_success_default: string;
    status_success_subdued: string;
    status_error_default: string;
    status_error_subdued: string;
    status_warning_default: string;
    status_warning_subdued: string;
    status_info_default: string;
    status_info_subdued: string;
    status_neutral_default: string;
  };
}

interface BrandContextType {
  brand: BrandType | null;
  colors: Colors;
  onUpdateBrand: (brand: Partial<BrandType | null>) => void;
  onResetBrand: (brand: Partial<BrandType | null>) => void;
  onParentUpdateBrand: (brand: BrandType) => void;
  onChangeRoute: (route: string) => void;
  previewRef: React.RefObject<HTMLIFrameElement>;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const BrandProvider: React.FC<{
  children: React.ReactNode;
  brand: BrandType | null;
}> = ({ children, brand: _brand }) => {
  const [brand, setBrand] = useState<BrandType | null>(_brand);
  const previewRef = useRef<HTMLIFrameElement>(null);

  const onUpdateBrand = useCallback((brand: Partial<BrandType | null>) => {
    if (previewRef.current) {
      previewRef.current.contentWindow?.postMessage(
        { type: POST_MESSAGE_EVENTS.update, brand },
        '*',
      );
    }
  }, []);

  const onResetBrand = useCallback((brand: Partial<BrandType | null>) => {
    if (previewRef.current) {
      previewRef.current.contentWindow?.postMessage(
        { type: POST_MESSAGE_EVENTS.reset, brand },
        '*',
      );
    }
  }, []);

  const onChangeRoute = useCallback((route: string) => {
    if (previewRef.current) {
      previewRef.current.contentWindow?.postMessage(
        { type: POST_MESSAGE_EVENTS.changeRoute, route },
        '*',
      );
    }
  }, []);

  const onParentUpdateBrand = useCallback(
    (brand: BrandType) => setBrand(brand),
    [],
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === POST_MESSAGE_EVENTS.update)
        setBrand((prev) => {
          return { ...prev, ...event.data.brand };
        });
      if (event.data.type === POST_MESSAGE_EVENTS.reset)
        setBrand(event.data.brand);
      if (event.data.type === POST_MESSAGE_EVENTS.changeRoute) {
        navigation(event.data.route);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // for style components
  const _colors = useMemo(() => {
    if (brand?.mode === 'light') return theme.lightColors;
    return theme.darkColors;
  }, [brand]);

  const updateTheme = useCallback(() => {
    if (brand?.fontFamily) applyFont(brand.fontFamily);
    if (brand?.title) document.title = brand.title;
    if (brand) {
      applyFavicon(brand);
      applyColors(brand);
    }
  }, [brand]);

  useEffect(() => updateTheme(), [updateTheme]);

  return (
    <BrandContext.Provider
      value={{
        brand,
        colors: _colors,
        onUpdateBrand,
        onResetBrand,
        onParentUpdateBrand,
        onChangeRoute,
        previewRef,
      }}
    >
      {children}
    </BrandContext.Provider>
  );
};

export const useBrandContext = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrandContext must be used within a BrandProvider');
  }
  return context;
};
